import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Sketch from '@uiw/react-color-sketch';
import { Button, Overlay } from 'react-bootstrap';
import './LomavisSketchColorPicker.css';

/**
 * A colour that is finished being written. Typing a hex value passes through
 * `#1`, `#12`, `#123a` and the like, and none of those are what the user
 * means — only a complete three- or six-digit hex is published.
 */
const COMPLETE_HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export type LomavisSketchColorPickerProps = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  compact?: boolean;
};

/** Hex-only Sketch picker rendered in a viewport-level, accessible popover. */
export const LomavisSketchColorPicker: React.FC<
  LomavisSketchColorPickerProps
> = ({ value, onChange, ariaLabel, disabled = false, compact = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const draftValueRef = useRef(value);
  const committedValueRef = useRef(value);
  const releaseCommitTimer = useRef<number>();
  const liveCommitFrame = useRef<number>();
  // What the colour was when the picker opened. Changes reach the layer as
  // they are made now, so cancelling has to put this back rather than simply
  // stop publishing.
  const openedValueRef = useRef(value);
  const target = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const popupId = `lomavis-sketch-color-picker-${useId().replace(/:/g, '')}`;

  const commitDraft = useCallback(() => {
    if (!COMPLETE_HEX.test(draftValueRef.current)) return;
    if (draftValueRef.current === committedValueRef.current) return;
    committedValueRef.current = draftValueRef.current;
    onChange(draftValueRef.current);
  }, [onChange]);

  // Published as the colour is chosen, so the text shows it without waiting
  // for the picker to be dismissed. Coalesced to a frame: dragging across the
  // saturation square fires a change per pointer move, and the artboard should
  // redraw once per frame rather than once per event.
  const scheduleLiveCommit = useCallback(() => {
    if (liveCommitFrame.current !== undefined) return;
    liveCommitFrame.current = window.requestAnimationFrame(() => {
      liveCommitFrame.current = undefined;
      commitDraft();
    });
  }, [commitDraft]);

  useEffect(
    () => () => {
      if (liveCommitFrame.current !== undefined)
        window.cancelAnimationFrame(liveCommitFrame.current);
    },
    []
  );

  const closePicker = useCallback(() => {
    commitDraft();
    setShowPicker(false);
    window.requestAnimationFrame(() => target.current?.focus());
  }, [commitDraft]);

  const cancelPicker = useCallback(() => {
    const opened = openedValueRef.current;
    setDraftValue(opened);
    draftValueRef.current = opened;
    if (committedValueRef.current !== opened) {
      committedValueRef.current = opened;
      onChange(opened);
    }
    setShowPicker(false);
    window.requestAnimationFrame(() => target.current?.focus());
  }, [onChange]);

  const scheduleReleaseCommit = useCallback(() => {
    window.clearTimeout(releaseCommitTimer.current);
    // UIW presets publish their color from `click`, which fires after
    // `mouseup`/`touchend`. Defer so both drag and click changes are present.
    releaseCommitTimer.current = window.setTimeout(commitDraft, 0);
  }, [commitDraft]);

  useEffect(() => {
    // Only a change from elsewhere. Our own commit comes back as this prop,
    // and adopting it would overwrite a draft the pointer has already moved on
    // from.
    if (value === committedValueRef.current) return;
    setDraftValue(value);
    draftValueRef.current = value;
    committedValueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!showPicker) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      cancelPicker();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [cancelPicker, showPicker]);

  useEffect(() => {
    if (!showPicker) return undefined;
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Node)) return;
      if (
        target.current?.contains(eventTarget) ||
        popup.current?.contains(eventTarget)
      )
        return;
      closePicker();
    };
    // Bootstrap's rootClose can see the opening click as outside the portaled
    // overlay in the Django application shell. Capture later pointer events
    // ourselves, after the initial click has completed.
    document.addEventListener('pointerdown', closeOnOutsidePointerDown);
    return () =>
      document.removeEventListener('pointerdown', closeOnOutsidePointerDown);
  }, [closePicker, showPicker]);

  useEffect(() => {
    if (!showPicker) return undefined;
    window.addEventListener('mouseup', scheduleReleaseCommit);
    window.addEventListener('touchend', scheduleReleaseCommit);
    return () => {
      window.removeEventListener('mouseup', scheduleReleaseCommit);
      window.removeEventListener('touchend', scheduleReleaseCommit);
      window.clearTimeout(releaseCommitTimer.current);
    };
  }, [scheduleReleaseCommit, showPicker]);

  useEffect(() => {
    if (disabled && showPicker) cancelPicker();
  }, [cancelPicker, disabled, showPicker]);

  return (
    <div className={compact ? 'lomavis-sketch-color-picker-compact' : ''}>
      <Button
        ref={target}
        type="button"
        variant="light"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={showPicker}
        aria-controls={showPicker ? popupId : undefined}
        onClick={() => {
          if (showPicker) {
            closePicker();
            return;
          }
          setDraftValue(value);
          draftValueRef.current = value;
          committedValueRef.current = value;
          openedValueRef.current = value;
          setShowPicker(true);
        }}
        className={
          compact
            ? 'lomavis-sketch-color-picker-compact-toggle bg-white shadow-none'
            : 'lomavis-sketch-color-picker-toggle d-flex w-100 align-items-center gap-2 rounded-3 border bg-white px-3 py-2 text-start shadow-none'
        }
      >
        {compact ? (
          <span
            className="lomavis-sketch-color-picker-icon"
            style={{ borderBottomColor: value }}
          >
            A
          </span>
        ) : (
          <>
            <span
              className="lomavis-sketch-color-picker-swatch"
              style={{ backgroundColor: value }}
            />
            <span className="text-uppercase">{value}</span>
          </>
        )}
      </Button>
      <Overlay
        target={target.current}
        show={showPicker}
        placement={compact ? 'bottom-end' : 'bottom-start'}
        popperConfig={{ strategy: 'fixed' }}
        onEntered={() =>
          popup.current?.querySelector<HTMLElement>('input, button')?.focus()
        }
      >
        {({
          ref: overlayRef,
          placement,
          arrowProps: _arrowProps,
          show: _show,
          popper: _popper,
          className: overlayClassName,
          ...overlayProps
        }) => (
          <div
            {...overlayProps}
            ref={(node) => {
              popup.current = node;
              overlayRef(node);
            }}
            id={popupId}
            role="dialog"
            aria-label={ariaLabel}
            data-popper-placement={placement}
            className={`${
              typeof overlayClassName === 'string' ? overlayClassName : ''
            } lomavis-sketch-color-picker-menu border-0 bg-white p-2 shadow`}
          >
            <Sketch
              color={draftValue}
              disableAlpha
              width={260}
              onChange={(color) => {
                setDraftValue(color.hex);
                draftValueRef.current = color.hex;
                scheduleLiveCommit();
              }}
            />
          </div>
        )}
      </Overlay>
    </div>
  );
};
